import { Component, ChangeDetectorRef, DestroyRef, ElementRef, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SharedService } from 'src/app/shared/services/shared.service';
import { combineLatest, Subscription, firstValueFrom } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { Answers, FFLMonthCharacters, Questions } from 'src/app/shared/models/constants';
import { AtfFfl } from 'src/app/shared/models/atf-ffl';
import { FFL } from 'src/app/shared/models/general';
import { calculateFFLExpirationDate, formatFfl } from 'src/app/shared/functions';
import { CompanyInfo, User } from 'src/app/shared/models/company';

import { NgxMaskDirective } from 'ngx-mask';
import { BottomFooterComponent } from '../bottom-footer/bottom-footer.component';
import { TopNavbarComponent } from '../top-navbar/top-navbar.component';
import { SubscriptionService } from '../subscription.service';
import {
  loadStripe,
  StripeCheckoutElementsSdk,
  StripeCheckoutLoadActionsSuccess,
  StripePaymentElement,
} from '@stripe/stripe-js';

@Component({
  selector: 'app-sign-up',
  standalone: true,
  templateUrl: './sign-up.component.html',
  styleUrl: './sign-up.component.scss',
  imports: [ReactiveFormsModule, FormsModule, NgxMaskDirective, TopNavbarComponent, BottomFooterComponent]
})
export class SignUpComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly subscriptions = inject(SubscriptionService);
  private readonly cdr = inject(ChangeDetectorRef);
  private checkoutSdk?: StripeCheckoutElementsSdk;
  private checkoutActions?: StripeCheckoutLoadActionsSuccess;
  private paymentElement?: StripePaymentElement;

  signUpForm: FormGroup;
  showPassword = false;
  showConfirmPassword = false;
  isSubmitting = false;
  currentStep = 1;
  selectedPlan = 'Professional';
  selectedPrice = '$29.99';
  selectedSuffix = '/month';
  checkoutError = '';
  checkoutEmail = '';
  checkoutSubmitting = false;
  checkoutTrial = true;
  checkoutReady = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    public sharedService: SharedService
  ) {
    this.signUpForm = this.fb.group({
      // Account Info
      username: [''],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', Validators.required],
      
      // User Info
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],

      // Company Info
      companyName: [''],
      legalName: [''],
      fflNumber: [''],
      fflExpiration: [''],
      ein: [''],
      address: [''],
      address2: [''],
      city: [''],
      state: [''],
      zip: [''],
      phoneNumber: [''],
      companyEmail: [''],
      companyWebsite: [''],
      
      // Subscription
      acceptTerms: [false, Validators.requiredTrue],

      // Payment
      cardName: [''],
      cardNumber: [''],
      expiry: [''],
      cvc: [''],
      sameAsBusiness: [true],
      billingAddress: [''],
      billingCity: [''],
      billingState: [''],
      billingZip: ['']
    }, { validators: this.passwordMatchValidator });

    // Handle FFL Confirmation Result
    this.sharedService.yes_no_submit$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        if (
          response['answer'] === Answers.yes &&
          response['question'] === Questions.correct_ffl
        ) {
          const info = response['optional_details'] as FFL;
          this.patchFfl(info);
        }
      });

    // Auto-generate username
    combineLatest([
      this.signUpForm.get('firstName').valueChanges.pipe(startWith('')),
      this.signUpForm.get('lastName').valueChanges.pipe(startWith('')),
    ]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(([first, last]) => {
      const initial = (first || '').trim().charAt(0).toLowerCase();
      const surname = (last || '').trim().toLowerCase().replace(/[^a-z]/g, '');
      const generated = (initial || surname) ? `${initial}${surname}` : '';
      this.signUpForm.get('username').setValue(generated, { emitEvent: false });
    });
  }

  ngOnInit(): void {
    const token = sessionStorage.getItem('plan_selected');
    if (!token) {
      this.router.navigate(['/landing-page']);
      return;
    }
    sessionStorage.removeItem('plan_selected');

    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      if (params['plan']) this.selectedPlan = params['plan'];
      if (params['price']) {
        this.selectedPrice = params['price'];
        this.selectedSuffix = '/month';
      }
      if (params['trial'] !== undefined) this.checkoutTrial = params['trial'] !== 'false';
    });
  }

  ngOnDestroy(): void {
    this.destroyCheckout();
  }

  async initStripeCheckout(): Promise<void> {
    this.checkoutError = '';
    this.checkoutReady = false;
    const planKey = this.selectedPlan.toLowerCase() as any;
    try {
      const { clientSecret, publishableKey } = await this.subscriptions.createCheckout(planKey, this.checkoutTrial);
      this.cdr.detectChanges();
      const stripe = await loadStripe(publishableKey);
      if (!stripe) throw new Error('Stripe.js could not be loaded.');
      this.checkoutSdk = stripe.initCheckoutElementsSdk({
        clientSecret,
        elementsOptions: {
          appearance: {
            theme: 'night',
            variables: {
              colorPrimary: '#ef2b2b',
              colorBackground: '#101010',
              colorText: '#f5f5f5',
              colorDanger: '#ff5d5d',
              fontFamily: 'Inter, Arial, sans-serif',
              borderRadius: '10px',
            },
          },
        },
      });
      const mountPoint = this.host.nativeElement.querySelector<HTMLElement>('#embedded-checkout');
      if (!mountPoint) throw new Error('Checkout container is unavailable.');
      this.paymentElement = this.checkoutSdk.createPaymentElement({ layout: 'accordion' });
      this.paymentElement.mount(mountPoint);
      const actionsResult = await this.checkoutSdk.loadActions();
      if (actionsResult.type === 'error') throw new Error(actionsResult.error.message);
      this.checkoutActions = actionsResult.actions;
      this.checkoutReady = true;
      this.cdr.detectChanges();
    } catch (error: any) {
      this.destroyCheckout();
      this.checkoutError = error?.error?.message || 'Checkout is temporarily unavailable. Please try again.';
    }
  }

  async submitCheckout(): Promise<void> {
    if (!this.checkoutActions || this.checkoutSubmitting) return;
    this.checkoutError = '';
    this.checkoutSubmitting = true;
    try {
      const emailResult = await this.checkoutActions.updateEmail(this.checkoutEmail.trim());
      if (emailResult.type === 'error') throw new Error(emailResult.error.message);
      const result = await this.checkoutActions.confirm({ redirect: 'if_required' });
      if (result.type === 'error') throw new Error(result.error.message);
      window.location.assign('/login?checkout=success');
    } catch (error: any) {
      this.checkoutError = error?.message || 'Payment could not be submitted. Please check your details.';
    } finally {
      this.checkoutSubmitting = false;
    }
  }

  private destroyCheckout(): void {
    this.paymentElement?.destroy();
    this.paymentElement = undefined;
    this.checkoutActions = undefined;
    this.checkoutSdk = undefined;
    this.checkoutReady = false;
  }

  passwordMatchValidator(g: FormGroup) {
    const password = g.get('password');
    const confirmPassword = g.get('confirmPassword');
    if (!password || !confirmPassword) return null;

    if (password.value !== confirmPassword.value) {
      return { mismatch: true };
    }
    return null;
  }

  async nextStep() {
    if (this.currentStep === 1) {
      if (!this.isAccountInfoValid()) {
        return;
      }
      const email = this.signUpForm.get('email').value;
      try {
        const response = await firstValueFrom(this.sharedService.checkEmailExists(email));
        if (response.exists) {
          this.sharedService.raiseErrorToast("An account with this email address already exists.");
          return;
        }
      } catch (err) {
        // Email check failed (server unavailable etc.) — log and proceed rather than blocking the user
        console.warn("Email uniqueness check failed, proceeding anyway:", err);
      }
      this.currentStep = 2;
      window.scrollTo(0, 0);
      this.cdr.detectChanges();
    } else if (this.currentStep === 2 && this.isCompanyInfoValid()) {
      this.currentStep = 3;
      window.scrollTo(0, 0);
      this.initStripeCheckout();
    }
  }

  prevStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
      window.scrollTo(0,0);
    }
  }

  isAccountInfoValid(): boolean {
    const fields = ['email', 'password', 'confirmPassword', 'firstName', 'lastName'];
    let valid = true;
    fields.forEach(f => {
      const control = this.signUpForm.get(f);
      control.markAsTouched();
      if (control.invalid) {
        valid = false;
      }
    });
    const password = this.signUpForm.get('password').value;
    if (password && !this.hasSpecialCharacter(password)) {
      this.signUpForm.get('password').setErrors({ missingSpecialChar: true });
      valid = false;
    }

    if (this.signUpForm.hasError('mismatch')) {
      valid = false;
    }

    return valid;
  }

  hasSpecialCharacter(str: string): boolean {
    const specialCharPattern = /[!@#$%^&*(),.?":{}|<>]/;
    return specialCharPattern.test(str);
  }

  isCompanyInfoValid(): boolean {
    // Relaxed for design testing
    return true;
  }

  onSubmit() {
    if (this.signUpForm.valid) {
      this.isSubmitting = true;
      
      const formValues = this.signUpForm.value;

      // 1. Create User Instance
      const newUser = new User();
      newUser.name_first = formValues.firstName;
      newUser.name_last = formValues.lastName;
      newUser.username = formValues.username;
      newUser.password = formValues.password;
      newUser.email = formValues.email;
      newUser.role = "Admin";
      newUser.position_title = "Store Owner";
      newUser.signature = "";

      // 2. Create Company Profile Instance
      const newCompanyProfile = new CompanyInfo();
      newCompanyProfile.company_name = formValues.companyName;
      newCompanyProfile.legal_name = formValues.legalName;
      newCompanyProfile.ein = formValues.ein;
      newCompanyProfile.address = formValues.address;
      newCompanyProfile.address2 = formValues.address2;
      newCompanyProfile.city = formValues.city;
      newCompanyProfile.state = formValues.state;
      newCompanyProfile.zip_code = formValues.zip;
      newCompanyProfile.phone_number = formValues.phoneNumber;
      newCompanyProfile.company_email = formValues.companyEmail;
      newCompanyProfile.customer_facing_email = formValues.companyEmail;
      newCompanyProfile.website = formValues.companyWebsite;
      newCompanyProfile.ffl = formValues.fflNumber;
      newCompanyProfile.ffl_expiration_date = formValues.fflExpiration;
      
      // Set 3310 Autofill defaults
      newCompanyProfile.autofill_form_3310.agency_name = "ADA COUNTY SHERIFFS";
      newCompanyProfile.autofill_form_3310.address = "7200 BARRISTER DR";
      newCompanyProfile.autofill_form_3310.city = "BoisE";
      newCompanyProfile.autofill_form_3310.state = "IDAHO";
      newCompanyProfile.autofill_form_3310.agency_email = "sheriff@adacounty.id.gov";
      newCompanyProfile.autofill_form_3310.atf_email = "multiplehandgunsalesforms@atf.gov";

      // Execute registration via the shared service
      this.sharedService.register({ newUser, newCompanyProfile })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            console.log("Registration Successful!", response);
            this.sharedService.raiseSuccessToast("Account activated! Please login to continue.");
            this.isSubmitting = false;
            
            // Navigate to login
            this.router.navigate(['/login']);
          },
          error: (error) => {
            console.error("Registration Failed:", error);
            const errorMessage = error.error?.message || "Registration failed. Please try again.";
            this.sharedService.raiseErrorToast(errorMessage);
            this.isSubmitting = false;
          }
        });
    } else {
      Object.keys(this.signUpForm.controls).forEach(key => {
        const control = this.signUpForm.get(key);
        control.markAsTouched();
      });
      this.sharedService.raiseErrorToast("Please complete all required fields.");
    }
  }

  private markAllTouched(group: FormGroup) {
    Object.values(group.controls).forEach(control => {
      control.markAsTouched();
      if ((control as any).controls) {
        this.markAllTouched(control as FormGroup);
      }
    });
  }

  togglePassword() {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPassword() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  verifyFFL() {
    let new_ffl = this.signUpForm.value.fflNumber;
    if (!new_ffl) return;
    const stripped = new_ffl.replace(/-/g, '').toUpperCase();
    console.log('FFL raw:', new_ffl, 'len:', new_ffl.length, 'stripped:', stripped, 'stripped len:', stripped.length);
    // FFL is 15 chars stripped: pos 0=region, 1-2=district, 3-5=county, 6-7=year, 8-9=month letters, 10-14=serial
    const isComplete = stripped.length === 15;
    if (isComplete) {
      // Index 8 = license type digit, Index 9 = month letter (A-M, no I)
      if (FFLMonthCharacters.includes(stripped.substring(9, 10))) {
        this.fetchFflDetails(stripped);
      } else {
        this.sharedService.raiseErrorToast(
          'FFL Month is not valid. Please enter a valid FFL.'
        );
        this.signUpForm.patchValue({
          fflNumber: null,
          fflExpiration: null,
        });
      }
    }
  }

  fetchFflDetails(ffl: string) {
    this.sharedService
      .verifyFflAtfList(ffl)
      .subscribe({
        next: (response: { message: string; ffl: AtfFfl }) => {
          let fetched_ffl: FFL = new FFL();
          fetched_ffl.ffl = ffl;
          fetched_ffl.legal_name = response.ffl?.license_name?.toString() ?? '';
          fetched_ffl.business_name = response.ffl.business_name?.toString() ?? '';
          fetched_ffl.premise_street = response.ffl.premise_street?.toString() ?? '';
          fetched_ffl.premise_city = response.ffl.premise_city?.toString() ?? '';
          fetched_ffl.premise_state = response.ffl.premise_state?.toString() ?? '';
          fetched_ffl.premise_zip_code = response.ffl.premise_zip_code?.toString() ?? '';
          fetched_ffl.phone_number = response.ffl.voice_telephone?.toString() ?? '';
          fetched_ffl.email = null;
          fetched_ffl.notes = null;
          fetched_ffl.issue_date = response.ffl.loa_issue_date
            ? response.ffl.loa_issue_date.toString()
            : null;
          fetched_ffl.expiration_date = response.ffl.loa_expiration_date
            ? response.ffl.loa_expiration_date.toString()
            : null;

          // Patch directly — no confirmation modal needed in sign-up flow
          this.patchFfl(fetched_ffl);
          this.sharedService.raiseSuccessToast(`FFL verified! Company info pre-filled for ${fetched_ffl.business_name}.`);
        },
        error: (err) => {
          console.error('Error fetching FFL details:', err);
          if (err.status === 404) {
            this.sharedService.raiseWarningToast(
              'Could not find a matching FFL number. Please check the number and try again.'
            );
          } else {
            this.sharedService.raiseErrorToast(
              'An unexpected error occurred while communicating with the server. Please try again later.'
            );
          }
        }
      });
  }

  patchFfl(response_details: FFL) {
    this.signUpForm.patchValue({
      companyName: response_details.business_name,
      legalName: response_details.legal_name,
      address: response_details.premise_street,
      city: response_details.premise_city,
      state: response_details.premise_state,
      zip: response_details.premise_zip_code,
      phoneNumber: response_details.phone_number,
      fflExpiration: (response_details.expiration_date && response_details.expiration_date.length > 5)
        ? response_details.expiration_date
        : calculateFFLExpirationDate(formatFfl(response_details.ffl)),
    });
    
    this.signUpForm.get('companyName').markAsDirty();
    this.signUpForm.get('companyName').markAsTouched();
    this.signUpForm.get('companyName').updateValueAndValidity();
  }

}
